import type { Features } from "@/libs/feature-toggle"
import type { ImageSource } from "@/libs/image"
import type { PerformanceEntry } from "@/libs/telemetry/performance"
import { Graph } from "../../framework"
import { type FaceDetection, create as createFaceMesh } from "../../modules/face-mesh/face-mesh"

export type { FaceDetection }

type Progress = { complete: number }

export type CreateOptions = {
  performance?: { child: () => PerformanceEntry }
  onProgress?: (progress: Progress) => void
  features?: Features
}

export const create = async (opts?: CreateOptions) => {
  // 1 for face mesh, treated as 70% of total
  // 1 for extra non-network tasks like models compilation, treated as  30% of total
  const progresses: number[] = [0, 0, 0]
  const onProgress = opts?.onProgress

  const notify = onProgress
    ? (index: number, { complete }: Progress) => {
        progresses[index] = complete

        onProgress({
          complete: progresses.reduce((acc, c, idx) => acc + c * (idx < 2 ? 0.7 : 0.3), 0),
        })
      }
    : undefined

  const graph = new Graph<[ImageSource], [FaceDetection]>()
  const faceMesh = await createFaceMesh(graph, {
    performance: opts?.performance?.child(),
    onProgress: notify && notify.bind(null, 0),
    features: opts?.features,
  }).catch((cause: unknown) => {
    throw new Error("Failed to create FaceMesh node", { cause })
  })

  if (notify) notify(1, { complete: 1 })

  const frame = graph.in(0)

  frame.connect(faceMesh.in(0))

  const detection = faceMesh.out(0)

  detection.connect(graph.out(0))

  return graph
}
